import { Grid, ItemSize } from '../types';

type GridConfig = {
  pattern: ItemSize[];
  withLinkExtraSpace: boolean;
};

export const DEFAULT_PATTERN: ItemSize[] = [4, 2, 5, 3];
const PATTERN_FOR_COLUMN_WITH_INCREASED_SIZE: ItemSize[] = [2, 3, 4, 3];

export const DEFAULT_CONFIG: GridConfig = {
  pattern: DEFAULT_PATTERN,
  withLinkExtraSpace: false,
};

/*
 * Generate column iterating ver given pattern
 */
export function generateColumnByPattern(columnSize: number, pattern: ItemSize[]): ItemSize[] {
  return [...Array(columnSize)].map((_, index) => {
    const patternElementIndex = index % pattern.length;
    return pattern[patternElementIndex];
  });
}

export function generateNextColumn(
  pattern: ItemSize[],
  columnSize: number,
  desiredItemsSizesSum: number,
  prevColumnGaps: number[],
): ItemSize[] {
  if (columnSize < 1) {
    return [];
  }

  const availableItemSizes = pattern.filter((size) => !prevColumnGaps.includes(size));

  for (const itemSize of availableItemSizes) {
    const newGaps = prevColumnGaps.map((gap) => gap - itemSize).filter((gap) => gap > 1);
    const nextSizes = generateNextColumn(
      pattern,
      columnSize - 1,
      desiredItemsSizesSum - itemSize,
      newGaps,
    );
    const generated = [itemSize, ...nextSizes];
    const generatedSum = generated.reduce((sum, size) => sum + size, 0);

    // Generation succeed if columns lengths are identical
    // If they not, lets try from beginning with next size
    if (desiredItemsSizesSum === generatedSum) {
      return generated;
    }
  }

  // All attempts to generate column failed, just generate column by pattern
  return generateColumnByPattern(columnSize, pattern);
}

/*
 * Reduce first item with size > 2, so we can fit 1 sized element
 */
export function reduceColumnSizeByOne(column: ItemSize[]) {
  const copy = [...column];
  const updateIndex = column.findIndex((size) => size > 2);
  copy[updateIndex] -= 1;
  return copy;
}

/*
 * Calculate gaps between column elements by add to current size all previous sizes
 * Exclude last element cos it is end of column not a gap
 * [4, 2, 5, 3] -> [4, 6, 11]
 */
export function calculateColumnGaps(sizes: number[]): number[] {
  return sizes.reduce<number[]>((acc, size, index) => {
    if (index === sizes.length - 1) {
      return acc;
    }
    const prevSize = acc[index - 1] ?? 0;
    return [...acc, size + prevSize];
  }, []);
}

export function generateGrid(
  gridSize: number,
  columnsCount: number,
  config: Partial<GridConfig> = {},
): Grid {
  const { pattern, withLinkExtraSpace } = { ...DEFAULT_CONFIG, ...config };
  const columnSize = Math.floor(gridSize / columnsCount);
  const columnsWithIncreasedSizeCount = gridSize % columnsCount;
  const [firstColumnSize, ...restColumnSizes] = Array(columnsCount)
    .fill(columnSize)
    .map((size, i) => (i < columnsWithIncreasedSizeCount ? size + 1 : size));
  const firstColumn = generateColumnByPattern(
    firstColumnSize,
    firstColumnSize > columnSize ? PATTERN_FOR_COLUMN_WITH_INCREASED_SIZE : pattern,
  );
  const desiredItemsSizesSum = firstColumn.reduce((sum, value) => sum + value, 0);
  const grid: ItemSize[][] = [firstColumn];

  // Generate next column from current one, so we don`t need iterate over last one column
  restColumnSizes.forEach((size, index) => {
    const current = grid[index];
    // Only add link to the last column
    const withPreservedSpaceForLink = withLinkExtraSpace && index === columnsCount - 2;
    const gaps = calculateColumnGaps(current);
    const next = generateNextColumn(
      size > columnSize ? PATTERN_FOR_COLUMN_WITH_INCREASED_SIZE : pattern,
      size,
      desiredItemsSizesSum,
      gaps,
    );

    // Add one size element to the end of column if we need space for a link
    grid.push(withPreservedSpaceForLink ? [...reduceColumnSizeByOne(next), 1] : next);
  });

  return grid;
}
